import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { NxPanel, NxButton, NxChip, NxTimeline, NxTimelineItem } from '../../components/nx';
import {
  X, Send, Mail, KeyRound, FileSignature, Calendar, Award, AlertTriangle,
  ChevronRight, FileText, ClipboardCheck, MessagesSquare, ShieldCheck, User,
  Phone, MapPin, BookOpen, Car, FileCheck2, Download, Eye, RefreshCw,
  Loader2, Plus, ScanLine, FlaskConical, GraduationCap, Lock, Unlock,
  CheckCircle2, XCircle, Save, AlertCircle, Wrench, Trash2, FileDown,
} from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

const formatDetail = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(d => (typeof d === 'string' ? d : d?.msg ? `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg}` : JSON.stringify(d)))
      .join('; ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
};

const api = async (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(formatDetail(data?.detail, `HTTP ${res.status}`));
    err.status = res.status;
    throw err;
  }
  return data;
};

const STAGE_LABEL = {
  APPLIED: 'Applied', UNDER_REVIEW: 'Under Review',
  APPROVED_FOR_TESTING: 'Approved Testing', TEST_IN_PROGRESS: 'Testing', AWAITING_TEST_REVIEW: 'Test Review',
  INTERVIEW_PENDING: 'Interview Pending', INTERVIEW_SCHEDULED: 'Interview Set',
  INTERVIEW_COMPLETED: 'Interview Done', PASSED_INTERVIEW: 'Passed Interview', FAILED_INTERVIEW: 'Failed Interview',
  AWAITING_DOCUMENTS: 'Docs Pending', DOCUMENT_REVIEW: 'Docs Review', FINAL_DECISION: 'Final Decision',
  HIRED: 'Hired', NOT_HIRED: 'Not Hired', ARCHIVED: 'Archived',
};

// -------------------------------------------------------------------
// Tabs
// -------------------------------------------------------------------
const TABS = [
  { id: 'application',   label: 'Application',   icon: FileText },
  { id: 'test',          label: 'Psych Exam',    icon: FlaskConical },
  { id: 'documents',     label: 'Documents',     icon: FileSignature },
  { id: 'training',      label: 'Training',      icon: GraduationCap },
  { id: 'interviews',    label: 'Interviews',    icon: MessagesSquare },
  { id: 'background',    label: 'Background',    icon: ShieldCheck },
  { id: 'activity',      label: 'Activity',      icon: ScanLine },
];

// -------------------------------------------------------------------
const Row = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-2 py-1.5 border-b border-slate-800 last:border-0">
    {Icon && <Icon size={14} className="text-slate-500 flex-shrink-0 mt-0.5" />}
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="text-sm text-slate-100 mt-0.5 break-words">{value || <span className="text-slate-600 italic">—</span>}</p>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="rounded-lg border border-slate-700 bg-[#0E1626] p-3">
    <h6 className="text-[10px] font-mono uppercase tracking-[0.20em] text-amber-300 mb-2">{title}</h6>
    <div className="space-y-0">{children}</div>
  </div>
);

// -------------------------------------------------------------------
// APPLICATION TAB
// -------------------------------------------------------------------
const APPLICATION_FIELDS = [
  ['date_of_birth', 'Date of Birth'],
  ['ssn_last4', 'SSN (last 4)'],
  ['drivers_license_number', 'Driver License #'],
  ['drivers_license_state', 'Driver License State'],
  ['address_line1', 'Address Line 1'],
  ['address_line2', 'Address Line 2'],
  ['city', 'City'],
  ['state', 'State'],
  ['postal_code', 'Postal Code'],
  ['emergency_contact_name', 'Emergency Contact Name'],
  ['emergency_contact_phone', 'Emergency Contact Phone'],
  ['emergency_contact_relationship', 'Emergency Contact Relationship'],
  ['license_type', 'PI License Type'],
  ['pi_license_number', 'PI License Number'],
  ['pi_license_state', 'PI License State'],
  ['license_expiration', 'License Expiration'],
  ['highest_education', 'Highest Education'],
  ['school_name', 'School Name'],
  ['prior_agency', 'Prior Agency'],
];

const ApplicationTab = ({ a, applicantId, onAfterChange, onOpenMessage }) => {
  const [showMissingInfo, setShowMissingInfo] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  if (!a) return null;
  const fmt = (v) => v == null || v === '' ? null : (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v));
  const list = (arr) => Array.isArray(arr) && arr.length ? arr.join(', ') : null;
  const fullAddr = [a.address_line1, a.address_line2, a.city, a.state, a.postal_code].filter(Boolean).join(', ');

  const togglePick = (k) => {
    const n = new Set(picked);
    if (n.has(k)) n.delete(k); else n.add(k);
    setPicked(n);
  };

  const sendMissingInfo = async () => {
    if (picked.size === 0 && !message.trim()) {
      toast.error('Pick at least one field or add a message');
      return;
    }
    setBusy(true);
    try {
      await api(`/api/admin/applicants/${applicantId}/request-missing-info`, {
        method: 'POST',
        body: JSON.stringify({
          fields: Array.from(picked),
          message: message.trim() || null,
          notify_via: 'both',
        }),
      });
      toast.success('Applicant notified — application unlocked for resubmission');
      setShowMissingInfo(false);
      setPicked(new Set());
      setMessage('');
      onAfterChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="rcc-tab-application">
      {/* Admin action bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap bg-amber-500/5 border border-amber-500/30 rounded p-2">
        <p className="text-[11px] text-amber-200">
          {a.application_status === 'AWAITING_APPLICANT_RESUBMIT'
            ? 'Awaiting applicant resubmission'
            : 'Need missing info? Use Send Back to reopen the application.'}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <NxButton variant="primary" className="nx-btn--sm" onClick={() => onOpenMessage?.()} data-testid="rcc-app-send-msg">
            <MessagesSquare size={14} /> Send Message
          </NxButton>
          <NxButton
            variant="secondary"
            className="nx-btn--sm"
            onClick={() => setShowMissingInfo(true)}
            data-testid="rcc-app-send-back"
          >
            <FileText size={14} /> Request Missing Info / Send Back
          </NxButton>
        </div>
      </div>

      {showMissingInfo && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="rcc-missing-info-panel">
          <p className="text-xs text-slate-200">Select which fields the applicant must complete on resubmission:</p>
          <div className="max-h-52 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1 border border-slate-700 rounded p-2 bg-[#0E1626]">
            {APPLICATION_FIELDS.map(([k, label]) => (
              <label key={k} className={`flex items-start gap-2 p-1.5 rounded cursor-pointer text-[12px] ${picked.has(k) ? 'bg-amber-500/15 text-white' : 'text-slate-300 hover:bg-slate-800/40'}`}>
                <input type="checkbox" checked={picked.has(k)} onChange={() => togglePick(k)} data-testid={`rcc-missing-cb-${k}`} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Optional message to applicant (e.g., 'Please correct your SSN — only enter the last 4 digits')"
            className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 min-h-[50px]"
            data-testid="rcc-missing-message"
          />
          <div className="flex items-center justify-end gap-2">
            <NxButton variant="ghost" className="nx-btn--sm" onClick={() => { setShowMissingInfo(false); setPicked(new Set()); setMessage(''); }}>Cancel</NxButton>
            <NxButton variant="primary" className="nx-btn--sm" onClick={sendMissingInfo} disabled={busy} data-testid="rcc-missing-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />} Send Back {picked.size > 0 ? `(${picked.size})` : ''}
            </NxButton>
          </div>
        </div>
      )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rcc-tab-application">
      {/* Iter122 — Applicant intake attachments (headshot + license) */}
      <Section title="Photos & License">
        <div className="grid grid-cols-3 gap-2">
          {[
            { key: 'photo_url', label: 'Headshot' },
            { key: 'license_front_url', label: 'License (Front)' },
            { key: 'license_back_url', label: 'License (Back)' },
          ].map(item => {
            const url = a[item.key];
            return (
              <div key={item.key} className="bg-slate-900/60 border border-slate-800 rounded p-2 flex flex-col items-center gap-1" data-testid={`applicant-${item.key}`}>
                {url ? (
                  <img src={url} alt={item.label} className="w-full aspect-square object-cover rounded" />
                ) : (
                  <div className="w-full aspect-square rounded bg-slate-800 flex items-center justify-center text-[10px] text-slate-500 uppercase">Not uploaded</div>
                )}
                <div className="text-[10px] text-slate-400 uppercase font-mono tracking-wider">{item.label}</div>
                {url && (
                  <div className="flex gap-1 w-full">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="flex-1 text-center text-[10px] bg-slate-700 hover:bg-slate-600 text-white rounded py-1"><Eye className="w-3 h-3 inline mr-0.5" /> View</a>
                    <a href={url} download className="flex-1 text-center text-[10px] bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded py-1" data-testid={`download-${item.key}`}><Download className="w-3 h-3 inline mr-0.5" /> Save</a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Applicant Identity">
        <Row label="Full Name" value={fmt(a.full_legal_name) || `${a.first_name || ''} ${a.last_name || ''}`.trim()} icon={User} />
        <Row label="Email" value={fmt(a.email)} icon={Mail} />
        <Row label="Phone" value={fmt(a.phone)} icon={Phone} />
        <Row label="Date of Birth" value={fmt(a.date_of_birth)} />
        <Row label="SSN (last 4)" value={fmt(a.ssn_last4)} />
        <Row label="Driver's License" value={[fmt(a.drivers_license_number), fmt(a.drivers_license_state)].filter(Boolean).join(' · ')} />
        <Row label="Address" value={fullAddr} icon={MapPin} />
        <Row label="Emergency Contact" value={[fmt(a.emergency_contact_name), fmt(a.emergency_contact_phone), fmt(a.emergency_contact_relationship)].filter(Boolean).join(' · ')} />
      </Section>

      <Section title="Position & Texas Licensing">
        <Row label="Position Applied" value={fmt(a.position_applied)} />
        <Row label="Employment Type" value={fmt(a.employment_type)} />
        <Row label="Desired Pay Rate" value={fmt(a.desired_pay_rate)} />
        <Row label="Earliest Start" value={fmt(a.earliest_start_date)} />
        <Row label="TX PI License?" value={fmt(a.holds_tx_pi_license)} />
        <Row label="License Type" value={fmt(a.license_type)} />
        <Row label="License #" value={[fmt(a.pi_license_number), fmt(a.pi_license_state)].filter(Boolean).join(' · ')} />
        <Row label="License Expiration" value={fmt(a.license_expiration)} />
        <Row label="Eligible to Register w/ TX DPS" value={fmt(a.eligible_to_register_tx_dps)} />
        <Row label="License Revoked / Suspended" value={fmt(a.license_revoked_or_suspended)} />
        {a.license_revocation_explanation && <Row label="Revocation Explanation" value={a.license_revocation_explanation} />}
      </Section>

      <Section title="Investigative Experience">
        <Row label="Years Experience" value={fmt(a.years_investigative_experience)} />
        <Row label="Investigation Types" value={list(a.investigation_types)} />
        <Row label="Description" value={fmt(a.experience_description)} />
        <Row label="Specialized Skills" value={list(a.specialized_skills)} />
        <Row label="Additional Skills" value={fmt(a.additional_skills)} />
        <Row label="Relevant Certifications" value={fmt(a.relevant_certifications)} />
      </Section>

      <Section title="Education">
        <Row label="Highest Education" value={fmt(a.highest_education)} icon={BookOpen} />
        <Row label="School" value={fmt(a.school_name)} />
        <Row label="Area of Study" value={fmt(a.area_of_study)} />
      </Section>

      <Section title="Employment History">
        <Row label="Prior Years" value={fmt(a.prior_experience_years)} />
        <Row label="Prior Agency" value={fmt(a.prior_agency)} />
        <Row label="Prior Role" value={fmt(a.prior_role)} />
        <Row label="Tenure" value={[fmt(a.prior_employment_start), fmt(a.prior_employment_end)].filter(Boolean).join(' → ')} />
        <Row label="Supervisor" value={[fmt(a.prior_supervisor_name), fmt(a.prior_supervisor_phone)].filter(Boolean).join(' · ')} />
        <Row label="Reason for Leaving" value={fmt(a.prior_reason_for_leaving)} />
      </Section>

      <Section title="Military / LE Background">
        <Row label="Military?" value={fmt(a.has_military_background)} />
        <Row label="Branch / Rank / Years" value={[fmt(a.military_branch), fmt(a.military_rank), fmt(a.military_service_years)].filter(Boolean).join(' · ')} />
        <Row label="Law Enforcement?" value={fmt(a.has_law_enforcement_experience)} />
        <Row label="Agency / Role / Years" value={[fmt(a.le_agency), fmt(a.le_role), fmt(a.le_service_years)].filter(Boolean).join(' · ')} />
      </Section>

      <Section title="Vehicle & Technology">
        <Row label="Reliable Vehicle" value={fmt(a.has_reliable_vehicle)} icon={Car} />
        <Row label="Vehicle" value={[fmt(a.vehicle_make_model), fmt(a.vehicle_year), fmt(a.vehicle_license_plate)].filter(Boolean).join(' · ')} />
        <Row label="Equipment Owned" value={list(a.equipment_owned)} />
        <Row label="Case Mgmt" value={fmt(a.tech_case_mgmt)} />
        <Row label="Surveillance Cameras" value={fmt(a.tech_surveillance_cameras)} />
        <Row label="GPS Devices" value={fmt(a.tech_gps_devices)} />
        <Row label="Evidence Storage" value={fmt(a.tech_evidence_storage)} />
        <Row label="Report Software" value={fmt(a.tech_report_software)} />
        <Row label="Software Used" value={fmt(a.investigative_software_used)} />
      </Section>

      <Section title="12A. Availability & Communication">
        {/* Iter147 — 7-column schedule grid instead of comma-separated days */}
        {(() => {
          const DAYS = [
            { key: 'MON', label: 'Mon' }, { key: 'TUE', label: 'Tue' },
            { key: 'WED', label: 'Wed' }, { key: 'THU', label: 'Thu' },
            { key: 'FRI', label: 'Fri' }, { key: 'SAT', label: 'Sat' },
            { key: 'SUN', label: 'Sun' },
          ];
          const avail = new Set((a.availability_days || []).map(d => String(d).toUpperCase().slice(0, 3)));
          const shifts = Array.isArray(a.availability_shifts) ? a.availability_shifts : [];
          return (
            <div className="rounded border border-slate-700/60 bg-[#0B1220] p-2 mb-2" data-testid="app-12a-grid">
              <div className="grid grid-cols-7 gap-1">
                {DAYS.map(d => {
                  const on = avail.has(d.key);
                  return (
                    <div
                      key={d.key}
                      className={`rounded border px-1 py-2 text-center ${on
                        ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200'
                        : 'border-slate-700/60 bg-slate-900/40 text-slate-500'}`}
                      data-testid={`app-12a-${d.key}-${on ? 'on' : 'off'}`}
                    >
                      <div className="text-[10px] font-mono tracking-wider uppercase">{d.label}</div>
                      <div className="text-[11px] font-semibold mt-0.5">
                        {on ? 'Available' : 'Off'}
                      </div>
                    </div>
                  );
                })}
              </div>
              {shifts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {shifts.map((s, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded border border-sky-500/40 bg-sky-500/10 text-sky-200">
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        <Row label="Max Weekly Hours" value={fmt(a.max_weekly_hours)} />
        <Row label="Willing to Travel (Multi-Day)" value={fmt(a.willing_to_travel)} />
        <Row label="Willing to Relocate" value={fmt(a.willing_to_relocate)} />
        <Row label="Languages Spoken" value={list(a.languages_spoken)} />
        <Row label="Languages Written" value={list(a.languages_written)} />
        <Row label="Availability Notes" value={fmt(a.availability_notes)} />
      </Section>

      {Array.isArray(a.work_history) && a.work_history.length > 0 && (
        <Section title="Additional Employment History">
          <div className="space-y-2">
            {a.work_history.map((w, i) => (
              <div key={i} className="rounded border border-slate-700/60 bg-[#0B1220] p-2">
                <div className="text-xs font-mono text-amber-300 mb-1">Entry #{i + 1}</div>
                <Row label="Employer" value={fmt(w.employer)} />
                <Row label="Role" value={fmt(w.role)} />
                <Row label="Tenure" value={[fmt(w.start_date), fmt(w.end_date)].filter(Boolean).join(' → ')} />
                <Row label="Supervisor" value={[fmt(w.supervisor_name), fmt(w.supervisor_phone)].filter(Boolean).join(' · ')} />
                <Row label="Reason for Leaving" value={fmt(w.reason_for_leaving)} />
                <Row label="May Contact?" value={fmt(w.may_contact)} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(a.education_history) && a.education_history.length > 0 && (
        <Section title="Additional Education">
          <div className="space-y-2">
            {a.education_history.map((e, i) => (
              <div key={i} className="rounded border border-slate-700/60 bg-[#0B1220] p-2">
                <div className="text-xs font-mono text-amber-300 mb-1">Entry #{i + 1}</div>
                <Row label="Institution" value={fmt(e.institution)} />
                <Row label="Degree" value={fmt(e.degree)} />
                <Row label="Field of Study" value={fmt(e.field_of_study)} />
                <Row label="Tenure" value={[fmt(e.start_date), fmt(e.end_date)].filter(Boolean).join(' → ')} />
                <Row label="GPA" value={fmt(e.gpa)} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(a.certifications_list) && a.certifications_list.length > 0 && (
        <Section title="Licensed Certifications">
          <div className="space-y-2">
            {a.certifications_list.map((c, i) => (
              <div key={i} className="rounded border border-slate-700/60 bg-[#0B1220] p-2">
                <div className="text-xs font-mono text-amber-300 mb-1">Entry #{i + 1}</div>
                <Row label="Certification" value={fmt(c.name)} />
                <Row label="Issuer" value={fmt(c.issuer)} />
                <Row label="License #" value={fmt(c.license_number)} />
                <Row label="Issued / Expires" value={[fmt(c.issued_date), fmt(c.expiration_date)].filter(Boolean).join(' → ')} />
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Eligibility & Background Disclosures">
        <Row label="Authorized to Work in US" value={fmt(a.authorized_to_work_us)} />
        <Row label="18 or Older" value={fmt(a.over_18)} />
        <Row label="Ever Terminated / Asked to Resign" value={fmt(a.ever_terminated_or_asked_to_resign)} />
        {a.termination_details && <Row label="Termination Details" value={a.termination_details} />}
        <Row label="Financial Judgments / Bankruptcy" value={fmt(a.financial_judgments_or_bankruptcy)} />
        {a.financial_details && <Row label="Financial Details" value={a.financial_details} />}
        <Row label="Substance Abuse Treatment" value={fmt(a.substance_abuse_treatment)} />
        {a.substance_abuse_details && <Row label="Substance Abuse Details" value={a.substance_abuse_details} />}
        <Row label="Can Pass Drug Test" value={fmt(a.can_pass_drug_test)} />
      </Section>

      <Section title="References">
        <Row label="Reference 1" value={[fmt(a.reference1_name), fmt(a.reference1_relationship), fmt(a.reference1_phone)].filter(Boolean).join(' · ')} />
        <Row label="Reference 2" value={[fmt(a.reference2_name), fmt(a.reference2_relationship), fmt(a.reference2_phone)].filter(Boolean).join(' · ')} />
        <Row label="Reference 3" value={[fmt(a.reference3_name), fmt(a.reference3_relationship), fmt(a.reference3_phone)].filter(Boolean).join(' · ')} />
        {a.references && <Row label="Notes" value={a.references} />}
      </Section>

      <Section title="Criminal History">
        <Row label="Felony Conviction" value={fmt(a.felony_conviction)} />
        <Row label="Misdemeanor A/B Conviction" value={fmt(a.misdemeanor_ab_conviction)} />
        <Row label="Currently Under Investigation" value={fmt(a.currently_under_investigation)} />
        <Row label="History Details" value={fmt(a.criminal_history_details)} />
      </Section>

      <Section title="Why Nexus">
        <Row label="Why Interested" value={fmt(a.why_interested)} />
      </Section>

      <Section title="Consents & Signature">
        <Row label="Background Check Consent" value={fmt(a.consent_background_check)} />
        <Row label="Driving Record Consent" value={fmt(a.consent_driving_record)} />
        <Row label="Reference Verification Consent" value={fmt(a.consent_reference_verification)} />
        <Row label="TX DPS Verification Consent" value={fmt(a.consent_tx_dps_verification)} />
        <Row label="Drug Test Consent" value={fmt(a.consent_drug_test)} />
        <Row label="Background Initials" value={fmt(a.consent_initials_background)} />
        <Row label="Confidentiality" value={fmt(a.consent_confidentiality)} />
        <Row label="Confidentiality Initials" value={fmt(a.consent_initials_confidentiality)} />
        <Row label="Certified True" value={fmt(a.certified_true)} />
        <Row label="Applicant Signature" value={fmt(a.applicant_signature)} />
        <Row label="Signed At" value={fmt(a.signed_at) || fmt(a.signature_date) || fmt(a.created_at)} />
      </Section>
    </div>
    </div>
  );
};

// -------------------------------------------------------------------
// TEST / PSYCH EXAM TAB
// -------------------------------------------------------------------
const TestTab = ({ a, applicantId, onAfterChange }) => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await api(`/api/admin/applicants/${applicantId}/test-review`);
        if (alive) setReview(r);
      } catch (e) { if (alive) setReview({ error: e.message }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [applicantId]);

  const sendTestInvite = async () => {
    setBusy(true);
    try {
      await api(`/api/admin/applicants/${applicantId}/notice`, {
        method: 'POST', body: JSON.stringify({ template: 'psych_test_notice' })
      });
      toast.success('Psych exam invitation sent via SMS + Email');
      onAfterChange?.();
    } catch (e) { toast.error(`Send failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const approveForTesting = async () => {
    setBusy(true);
    try {
      await api(`/api/applicants/${applicantId}/stage`, {
        method: 'PATCH', body: JSON.stringify({ stage: 'APPROVED_FOR_TESTING' })
      });
      toast.success('Approved for testing — portal unlocked');
      onAfterChange?.();
    } catch (e) { toast.error(`Approve failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const score = a?.test_score;
  const stage = a?.stage;
  const canSendInvite = ['APPROVED_FOR_TESTING', 'TEST_IN_PROGRESS', 'AWAITING_TEST_REVIEW'].includes(stage);
  const needsApproval = ['APPLIED', 'UNDER_REVIEW'].includes(stage);

  return (
    <div className="space-y-3" data-testid="rcc-tab-test">
      {needsApproval && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-3">
          <FlaskConical className="w-5 h-5 text-amber-300 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Applicant has not been approved for testing yet</p>
            <p className="text-xs text-slate-400 mt-1">Approving moves them to APPROVED_FOR_TESTING and unlocks the IPS-OIE psych exam in their portal.</p>
          </div>
          <NxButton variant="primary" onClick={approveForTesting} disabled={busy} data-testid="rcc-approve-testing">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight size={14} />} Approve & Unlock Exam
          </NxButton>
        </div>
      )}

      {canSendInvite && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-500/5 p-3 flex items-start gap-3">
          <Send className="w-5 h-5 text-blue-300 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Send (or resend) psych exam invitation</p>
            <p className="text-xs text-slate-400 mt-1">Delivers the test link to the applicant via Email + SMS.</p>
          </div>
          <NxButton variant="primary" onClick={sendTestInvite} disabled={busy} data-testid="rcc-send-test-invite">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />} Send Invite
          </NxButton>
        </div>
      )}

      <Section title="IPS-OIE Psychological Suitability Score">
        {loading ? (
          <p className="text-slate-400 text-sm italic flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading score…</p>
        ) : score ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">Weighted Score</p>
                <p className="text-3xl font-extrabold text-white">{Math.round(score.weighted_pct || 0)}%</p>
              </div>
              <NxChip tone={(score.weighted_pct || 0) >= 70 ? 'lime' : (score.weighted_pct || 0) >= 50 ? 'amber' : 'red'}>
                {(score.weighted_pct || 0) >= 70 ? 'RECOMMENDED' : (score.weighted_pct || 0) >= 50 ? 'BORDERLINE' : 'NOT RECOMMENDED'}
              </NxChip>
              {score.submitted_at && (
                <p className="text-xs text-slate-400 font-mono ml-auto">Submitted {new Date(score.submitted_at).toLocaleString()}</p>
              )}
            </div>
            {score.recommendation && (
              <Row label="Recommendation" value={score.recommendation} />
            )}
            {score.dimensions && Object.keys(score.dimensions).length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 mt-2 mb-1.5">Dimension Breakdown</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(score.dimensions).map(([k, v]) => (
                    <div key={k} className="rounded border border-slate-700 bg-[#0E1626] p-2">
                      <p className="text-[10px] font-mono uppercase text-slate-400">{k.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-bold text-white">{typeof v === 'number' ? `${Math.round(v)}%` : String(v)}</p>
                      {typeof v === 'number' && (
                        <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden mt-1">
                          <div className="h-full bg-amber-500" style={{ width: `${Math.round(v)}%` }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(score.scenarios) && score.scenarios.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 mt-2 mb-1.5">Scenario Responses ({score.scenarios.length})</p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {score.scenarios.map((sc, i) => (
                    <div key={i} className="rounded border border-slate-700 bg-[#0E1626] p-2">
                      <p className="text-[11px] font-semibold text-white">Q{i + 1}. {sc.prompt || sc.question || 'Scenario'}</p>
                      <p className="text-[11px] text-slate-300 mt-0.5"><span className="text-amber-300">Answer:</span> {sc.answer || sc.response || '—'}</p>
                      {sc.score != null && <p className="text-[11px] text-slate-400 mt-0.5">Score: {sc.score}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-slate-400 text-sm italic">No test score recorded yet.</p>
        )}
      </Section>

      {review && !review.error && review.review_notes && (
        <Section title="Reviewer Notes">
          <p className="text-sm text-slate-100">{review.review_notes}</p>
          {review.reviewed_by_name && <p className="text-xs text-slate-500 mt-1">— {review.reviewed_by_name} · {review.reviewed_at}</p>}
        </Section>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// DOCUMENTS TAB
// -------------------------------------------------------------------
const DocumentsTab = ({ applicantId, onAfterChange }) => {
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [note, setNote] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([
        api(`/api/admin/applicants/${applicantId}/document-assignments`).catch(() => ({ assignments: [] })),
        api('/api/admin/document-templates').catch(() => ({ templates: [] })),
      ]);
      setAssignments(a.assignments || []);
      setTemplates(t.templates || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [applicantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePick = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const sendSelected = async () => {
    if (picked.size === 0) { toast.error('Select at least one template'); return; }
    setBusy(true);
    try {
      const res = await api(`/api/admin/applicants/${applicantId}/send-templates`, {
        method: 'POST',
        body: JSON.stringify({ template_ids: Array.from(picked), note: note || null }),
      });
      toast.success(`Sent ${res.count} document(s) · skipped ${res.skipped || 0} duplicate(s)`);
      setShowSend(false);
      setPicked(new Set());
      setNote('');
      await load();
      onAfterChange?.();
    } catch (e) { toast.error(`Send failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const previewAssignment = async (asn, signed = false) => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/applicants/${applicantId}/document-assignments/${asn.id}/file?signed=${signed}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewTitle(`${asn.template_name} ${signed ? '· SIGNED' : '· Original'}`);
    } catch (e) { toast.error(`Preview failed: ${e.message}`); }
  };

  const downloadSigned = async (asn) => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/applicants/${applicantId}/document-assignments/${asn.id}/file?signed=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Signed PDF unavailable');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${asn.template_name.replace(/[^a-z0-9-_]+/gi, '_')}_signed.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const deleteAssignment = async (asn) => {
    if (!window.confirm(`Remove "${asn.template_name}" from this applicant? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api(`/api/admin/applicants/${applicantId}/document-assignments/${asn.id}`, { method: 'DELETE' });
      toast.success('Assignment removed');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="rcc-tab-documents">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h6 className="text-[10px] font-mono uppercase tracking-[0.20em] text-amber-300">DocuSign Templates · {assignments.length} on file</h6>
        <div className="flex items-center gap-2 flex-wrap">
          <NxButton variant="primary" className="nx-btn--sm" onClick={() => setShowSend(s => !s)} disabled={busy} data-testid="rcc-doc-open-send">
            <Send size={14} /> Send Documents
          </NxButton>
        </div>
      </div>

      {showSend && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-3" data-testid="rcc-doc-send-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-slate-300">Pick one or more uploaded templates to send to this applicant&apos;s portal for signing.</p>
            {templates.length === 0 && (
              <p className="text-xs text-amber-300">No templates yet. Upload PDFs at <span className="font-mono">/onboarding-documents</span>.</p>
            )}
          </div>
          {templates.length > 0 && (
            <>
              <div className="max-h-56 overflow-y-auto space-y-1.5 border border-slate-700 rounded p-2 bg-[#0E1626]" data-testid="rcc-doc-template-list">
                {templates.map(t => (
                  <label
                    key={t.id}
                    className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${picked.has(t.id) ? 'border-amber-400 bg-amber-500/10' : 'border-transparent hover:bg-slate-800/50'}`}
                    data-testid={`rcc-doc-tpl-${t.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={picked.has(t.id)}
                      onChange={() => togglePick(t.id)}
                      className="mt-1"
                      data-testid={`rcc-doc-tpl-cb-${t.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">{t.name}</p>
                      {t.description && <p className="text-[11px] text-slate-400">{t.description}</p>}
                      <p className="text-[10px] font-mono text-slate-500">{t.category} · {Math.round((t.file_size || 0) / 1024)}KB</p>
                    </div>
                  </label>
                ))}
              </div>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Optional note to applicant (e.g., 'Please complete by Friday')"
                className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 min-h-[50px]"
                data-testid="rcc-doc-note"
              />
            </>
          )}
          <div className="flex items-center gap-2 justify-end">
            <NxButton variant="ghost" className="nx-btn--sm" onClick={() => { setShowSend(false); setPicked(new Set()); setNote(''); }}>Cancel</NxButton>
            <NxButton variant="primary" className="nx-btn--sm" onClick={sendSelected} disabled={busy || picked.size === 0} data-testid="rcc-doc-send-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />} Send {picked.size > 0 ? `(${picked.size})` : ''}
            </NxButton>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm italic flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading documents…</p>
      ) : assignments.length === 0 ? (
        <p className="text-slate-400 text-sm italic">No documents sent yet. Click &quot;Send Documents&quot; to choose templates.</p>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => {
            const status = (a.status || 'PENDING').toUpperCase();
            const tone = status === 'SIGNED' ? 'lime' : status === 'PENDING' ? 'amber' : 'blue';
            return (
              <div key={a.id} className="rounded-lg border border-slate-700 bg-[#0E1626] p-3" data-testid={`rcc-doc-asn-${a.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white break-words">{a.template_name}</p>
                    <p className="text-[11px] font-mono text-slate-500">{a.category || 'general'} · {a.id.slice(0, 8)}</p>
                  </div>
                  <NxChip tone={tone}>{status}</NxChip>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-2 text-[11px] text-slate-300 font-mono">
                  {a.sent_at && <span>Sent: {new Date(a.sent_at).toLocaleString()}</span>}
                  {a.signed_at && <span className="text-emerald-300">Signed: {new Date(a.signed_at).toLocaleString()}</span>}
                  {a.signed_name && <span>Signer: {a.signed_name}</span>}
                </div>
                {a.note && <p className="text-[11px] text-slate-400 italic mt-1">Note: {a.note}</p>}
                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  <NxButton variant="ghost" className="nx-btn--sm" onClick={() => previewAssignment(a, false)} data-testid={`rcc-doc-preview-original-${a.id}`}>
                    <Eye size={14} /> Original
                  </NxButton>
                  {status === 'SIGNED' && (
                    <>
                      <NxButton variant="success" className="nx-btn--sm" onClick={() => previewAssignment(a, true)} data-testid={`rcc-doc-preview-signed-${a.id}`}>
                        <Eye size={14} /> Signed
                      </NxButton>
                      <NxButton variant="secondary" className="nx-btn--sm" onClick={() => downloadSigned(a)} data-testid={`rcc-doc-download-signed-${a.id}`}>
                        <Download size={14} /> Download
                      </NxButton>
                    </>
                  )}
                  {status === 'PENDING' && (
                    <NxButton variant="danger" className="nx-btn--sm" onClick={() => deleteAssignment(a)} disabled={busy} data-testid={`rcc-doc-delete-${a.id}`}>
                      <X size={14} /> Remove
                    </NxButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-[90] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-1 sm:p-2"
          onClick={(e) => { if (e.target === e.currentTarget) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}
          data-testid="rcc-doc-preview-modal"
        >
          <div className="bg-[#0B1220] border border-slate-700 rounded-lg w-full h-full sm:w-[95vw] sm:h-[95vh] max-w-none flex flex-col">
            <div className="border-b border-slate-700 px-4 py-2.5 flex items-center justify-between flex-shrink-0">
              <p className="text-sm text-white font-semibold truncate pr-2">{previewTitle}</p>
              <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="nx-btn nx-btn--icon" data-testid="rcc-doc-preview-close">
                <X size={16} />
              </button>
            </div>
            <iframe src={previewUrl} title="Document" className="flex-1 w-full bg-white" data-testid="rcc-doc-preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// INTERVIEWS TAB
// -------------------------------------------------------------------
const InterviewsTab = ({ applicantId, onAfterChange }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ scheduled_at: '', interviewer_name: '', mode: 'in_person', notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api(`/api/admin/applicants/${applicantId}/interviews`);
      setList(r.interviews || r || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [applicantId]);

  const schedule = async () => {
    if (!form.scheduled_at) { toast.error('Pick a date/time'); return; }
    setBusy(true);
    try {
      // Map UI form to backend schema (interview_type enum is uppercase).
      const payload = {
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        interviewer_name: form.interviewer_name || null,
        interview_type: (form.mode || 'in_person').toUpperCase(),
        location: form.notes || null,  // free-form "Calling from / link / address"
        notes: form.notes || null,
        duration_minutes: 45,
        send_invite_email: true,
      };
      await api(`/api/admin/applicants/${applicantId}/interviews`, {
        method: 'POST', body: JSON.stringify(payload),
      });
      toast.success('Interview scheduled');
      setShow(false);
      setForm({ scheduled_at: '', interviewer_name: '', mode: 'in_person', notes: '' });
      load();
      onAfterChange?.();
    } catch (e) { toast.error(`Schedule failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="rcc-tab-interviews">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h6 className="text-[10px] font-mono uppercase tracking-[0.20em] text-amber-300">Interviews</h6>
        <NxButton variant="primary" className="nx-btn--sm" onClick={() => setShow(s => !s)} data-testid="rcc-schedule-toggle">
          <Calendar size={14} /> Schedule Interview
        </NxButton>
      </div>

      {show && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
          <input
            type="datetime-local" value={form.scheduled_at}
            onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
            className="bg-[#0E1626] border border-slate-700 text-sm text-white rounded px-2 py-1.5 w-full"
            data-testid="rcc-interview-when"
          />
          <input
            type="text" placeholder="Interviewer name" value={form.interviewer_name}
            onChange={e => setForm({ ...form, interviewer_name: e.target.value })}
            className="bg-[#0E1626] border border-slate-700 text-sm text-white rounded px-2 py-1.5 w-full"
            data-testid="rcc-interview-who"
          />
          <select
            value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}
            className="bg-[#0E1626] border border-slate-700 text-sm text-white rounded px-2 py-1.5 w-full"
          >
            <option value="in_person">In Person</option>
            <option value="phone">Phone</option>
            <option value="video">Video</option>
          </select>
          <textarea
            placeholder="Internal notes" rows={2} value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            className="bg-[#0E1626] border border-slate-700 text-sm text-white rounded px-2 py-1.5 w-full"
          />
          <div className="flex items-center gap-2 justify-end">
            <NxButton variant="ghost" className="nx-btn--sm" onClick={() => setShow(false)}>Cancel</NxButton>
            <NxButton variant="primary" className="nx-btn--sm" onClick={schedule} disabled={busy} data-testid="rcc-interview-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />} Save
            </NxButton>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm italic flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : list.length === 0 ? (
        <p className="text-slate-400 text-sm italic">No interviews scheduled yet.</p>
      ) : (
        <div className="space-y-2">
          {list.map(iv => (
            <div key={iv.id} className="rounded-lg border border-slate-700 bg-[#0E1626] p-3" data-testid={`rcc-interview-${iv.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString() : '—'}</p>
                  <p className="text-[11px] font-mono text-slate-500">{iv.interviewer_name || 'Unassigned'} · {iv.mode || 'in_person'}</p>
                </div>
                <NxChip tone={iv.outcome === 'PASSED' ? 'lime' : iv.outcome === 'FAILED' ? 'red' : 'blue'}>
                  {(iv.status || iv.outcome || 'SCHEDULED').toString().toUpperCase()}
                </NxChip>
              </div>
              {iv.notes && <p className="text-xs text-slate-300 mt-1.5">{iv.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// BACKGROUND TAB (compact view of consents + risk + audit)
// -------------------------------------------------------------------
const BG_STATUSES = [
  ['NOT_STARTED', 'Not Started'],
  ['PENDING_REVIEW', 'Pending Review'],
  ['IN_PROGRESS', 'In Progress'],
  ['NEEDS_APPLICANT_INFO', 'Needs Applicant Info'],
  ['PASSED', 'Passed'],
  ['FAILED', 'Failed'],
  ['HOLD_FOR_SUPERVISOR_REVIEW', 'Hold for Supervisor Review'],
];

const BgField = ({ label, k, multiline, value, onSave }) => (
  <div className="space-y-1">
    <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
    {multiline ? (
      <textarea defaultValue={value || ''} onBlur={(e) => onSave({ [k]: e.target.value })}
        className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 min-h-[60px]"
        data-testid={`bg-${k}`} />
    ) : (
      <input defaultValue={value || ''} onBlur={(e) => onSave({ [k]: e.target.value })}
        className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2"
        data-testid={`bg-${k}`} />
    )}
  </div>
);

const BackgroundTab = ({ a, applicantId }) => {
  const [bg, setBg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await api(`/api/applicants/${applicantId}/background-review`);
        if (alive) setBg(r);
      } catch (e) { if (alive) toast.error(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [applicantId]);

  const save = async (patch) => {
    const next = { ...(bg || {}), ...patch };
    setBg(next);
    setSaving(true);
    try {
      await api(`/api/applicants/${applicantId}/background-review`, {
        method: 'PUT', body: JSON.stringify(patch),
      });
      toast.success('Background saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading background review…</p>;

  return (
    <div className="space-y-3" data-testid="rcc-tab-background">
      <Section title="Background Investigation">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1 sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Status</label>
            <select value={bg?.status || 'NOT_STARTED'} onChange={(e) => save({ status: e.target.value })}
              className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2"
              data-testid="bg-status">
              {BG_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <BgField label="Criminal / Civil Record Notes" k="criminal_civil_notes" multiline value={bg?.criminal_civil_notes} onSave={save} />
          <BgField label="License / Credential Verification" k="license_verification" value={bg?.license_verification} onSave={save} />
          <BgField label="Employment Verification" k="employment_verification" value={bg?.employment_verification} onSave={save} />
          <BgField label="Address Verification" k="address_verification" value={bg?.address_verification} onSave={save} />
          <BgField label="Reference Check Notes" k="reference_check_notes" multiline value={bg?.reference_check_notes} onSave={save} />
          <BgField label="Admin Notes" k="notes" multiline value={bg?.notes} onSave={save} />
          <BgField label="Final Recommendation" k="final_recommendation" multiline value={bg?.final_recommendation} onSave={save} />
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="bg-action-row">
          <NxButton variant="success" className="nx-btn--sm" onClick={() => save({ status: 'PASSED' })} disabled={saving} data-testid="bg-mark-passed">
            <CheckCircle2 size={14} /> Mark Passed
          </NxButton>
          <NxButton variant="danger" className="nx-btn--sm" onClick={() => save({ status: 'FAILED' })} disabled={saving} data-testid="bg-mark-failed">
            <XCircle size={14} /> Mark Failed
          </NxButton>
          <NxButton variant="secondary" className="nx-btn--sm" onClick={() => save({ status: 'HOLD_FOR_SUPERVISOR_REVIEW' })} disabled={saving} data-testid="bg-hold-review">
            <AlertCircle size={14} /> Hold for Review
          </NxButton>
          <NxButton variant="secondary" className="nx-btn--sm" onClick={() => save({ status: 'NEEDS_APPLICANT_INFO' })} disabled={saving} data-testid="bg-request-info">
            <Mail size={14} /> Request More Info
          </NxButton>
        </div>
      </Section>

      <Section title="Background Check Consents (from Application)">
        <Row label="Criminal Background" value={a?.consent_background_check ? 'Granted' : 'NOT granted'} />
        <Row label="Driving Record" value={a?.consent_driving_record ? 'Granted' : 'NOT granted'} />
        <Row label="Reference Verification" value={a?.consent_reference_verification ? 'Granted' : 'NOT granted'} />
        <Row label="TX DPS Verification" value={a?.consent_tx_dps_verification ? 'Granted' : 'NOT granted'} />
        <Row label="Confidentiality Agreement" value={a?.consent_confidentiality ? 'Signed' : 'NOT signed'} />
      </Section>

      <Section title="Risk Indicators (Disclosed)">
        <Row label="Felony Conviction" value={a?.felony_conviction ? 'YES' : 'No'} />
        <Row label="Misdemeanor A/B" value={a?.misdemeanor_ab_conviction ? 'YES' : 'No'} />
        <Row label="Currently Under Investigation" value={a?.currently_under_investigation ? 'YES' : 'No'} />
        <Row label="License Revoked / Suspended" value={a?.license_revoked_or_suspended ? 'YES' : 'No'} />
      </Section>
    </div>
  );
};

// -------------------------------------------------------------------
// ACTIVITY TAB (timeline)
// -------------------------------------------------------------------
const ActivityTab = ({ a }) => {
  const events = useMemo(() => {
    if (!a) return [];
    const out = [];
    if (a.created_at) out.push({ when: a.created_at, title: 'Application Received', tone: 'blue', body: `Submitted by ${a.first_name || ''} ${a.last_name || ''}`.trim() });
    (a.stage_history || []).forEach(h => out.push({
      when: h.at, title: `Moved to ${STAGE_LABEL[h.stage] || h.stage || ''}`,
      tone: h.stage === 'HIRED' ? 'lime' : h.stage === 'NOT_HIRED' ? 'red' : 'amber',
      body: h.by_name ? `By ${h.by_name}` : '',
    }));
    if (a.test_score?.submitted_at) out.push({
      when: a.test_score.submitted_at,
      title: `IPS-OIE Test · ${Math.round(a.test_score.weighted_pct || 0)}%`,
      tone: (a.test_score.weighted_pct || 0) >= 70 ? 'lime' : 'amber',
      body: a.test_score.recommendation || '',
    });
    (a.admin_notes || []).forEach(n => out.push({
      when: n.at || n.created_at, title: 'Admin Note',
      tone: 'blue', body: n.note || n.text || '',
    }));
    return out.sort((x, y) => (y.when || '').localeCompare(x.when || ''));
  }, [a]);

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })} · ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    } catch { return iso; }
  };

  return (
    <div data-testid="rcc-tab-activity">
      {events.length === 0 ? (
        <p className="text-slate-400 text-sm italic">No activity recorded.</p>
      ) : (
        <NxTimeline>
          {events.map((e, i) => (
            <NxTimelineItem key={i} when={fmt(e.when)} tone={e.tone} title={e.title}>
              {e.body}
            </NxTimelineItem>
          ))}
        </NxTimeline>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// TRAINING TAB — shows the applicant's Training Center progress once promoted
// -------------------------------------------------------------------
const TrainingTab = ({ a, applicantId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = React.useCallback(async () => {
    if (!applicantId) return;
    setLoading(true);
    try {
      const r = await api(`/api/applicants/${applicantId}/training-summary`);
      setData(r);
    } catch (e) { setData({ error: e.message }); }
    finally { setLoading(false); }
  }, [applicantId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return <p className="text-slate-400 text-sm italic flex items-center gap-2" data-testid="rcc-tab-training"><Loader2 className="w-4 h-4 animate-spin" /> Loading training progress…</p>;
  }
  if (!data) {
    return <p className="text-slate-400 text-sm italic" data-testid="rcc-tab-training">No training data available.</p>;
  }
  if (data.error) {
    return (
      <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4" data-testid="rcc-tab-training">
        <p className="text-sm text-rose-200">Unable to load training data: {data.error}</p>
        <p className="text-[11px] text-slate-500 mt-1">Refresh the page; if the error persists contact your administrator.</p>
      </div>
    );
  }
  if (!data.promoted) {
    return (
      <div className="rounded-lg border border-slate-700 bg-[#0E1626] p-6 text-center" data-testid="rcc-tab-training">
        <GraduationCap className="w-10 h-10 text-slate-500 mx-auto mb-2" />
        <p className="text-sm text-slate-300">{data.message || 'Training data is only available after the applicant has been hired.'}</p>
        <p className="text-xs text-slate-500 mt-1">Hire the applicant via the footer &apos;Hire&apos; action to enable their Training Center.</p>
      </div>
    );
  }
  if (data.broken_link) {
    const ra = data.assignments || [];
    const rp = data.progress || [];
    const rc = data.certs || [];
    const repair = async (path, label, needConfirm) => {
      if (needConfirm) {
        const t = window.prompt(`${label} — type CONFIRM to proceed`);
        if ((t || '').trim().toUpperCase() !== 'CONFIRM') return;
      } else if (!window.confirm(label + '?')) return;
      try {
        const r = await api(path, { method: 'POST' });
        toast[r.success ? 'success' : 'error'](r.message || (r.success ? 'Done' : 'Failed'));
        if (r.success) await reload();
      } catch (e) { toast.error(e.message); }
    };
    return (
      <div className="space-y-3" data-testid="rcc-tab-training">
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <p className="text-sm text-amber-200 font-semibold mb-1">User account missing — training records recovered through applicant profile</p>
          <p className="text-xs text-slate-300">{data.message}</p>
          <p className="text-[11px] font-mono text-slate-500 mt-2">applicant_id={data.applicant_id} → user_id={data.user_id} (not found)</p>
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <NxButton variant="primary" className="nx-btn--sm" onClick={() => repair(`/api/admin/applicants/${applicantId}/repair-user-link`, 'Try Repair User Link', false)} data-testid="rcc-train-repair-link">
              <Wrench size={14} /> Repair User Link
            </NxButton>
            <NxButton variant="danger" className="nx-btn--sm" onClick={() => repair(`/api/admin/applicants/${applicantId}/reprovision-user`, 'Re-Provision Portal User (creates a new user account)', true)} data-testid="rcc-train-reprovision">
              <Wrench size={14} /> Re-Provision User
            </NxButton>
          </div>
        </div>
        {(ra.length || rp.length || rc.length) ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rcc-train-recovered-stats">
            <div className="rounded border border-slate-700 bg-[#0E1626] p-2"><p className="text-[10px] uppercase text-slate-400">Recovered Assignments</p><p className="text-lg font-bold text-white">{ra.length}</p></div>
            <div className="rounded border border-slate-700 bg-[#0E1626] p-2"><p className="text-[10px] uppercase text-slate-400">Recovered Progress</p><p className="text-lg font-bold text-white">{rp.length}</p></div>
            <div className="rounded border border-slate-700 bg-[#0E1626] p-2"><p className="text-[10px] uppercase text-slate-400">Recovered Certs</p><p className="text-lg font-bold text-white">{rc.length}</p></div>
            <div className="rounded border border-slate-700 bg-[#0E1626] p-2"><p className="text-[10px] uppercase text-slate-400">Recoverable?</p><p className="text-lg font-bold text-emerald-300">{data.recoverable ? 'Yes' : 'No'}</p></div>
          </div>
        ) : (
          <p className="text-slate-400 text-sm italic">No training records found under applicant_id or prior user_id.</p>
        )}
      </div>
    );
  }

  const assignments = data.assignments || [];
  const certs = data.certs || [];
  const progress = data.progress || [];
  const progressByCourse = {};
  progress.forEach(p => { progressByCourse[p.course_id] = p; });

  const completed = data.stats?.completed ?? progress.filter(p => p.status === 'COMPLETED' || (p.completed_pct || 0) >= 100).length;
  const inProgress = data.stats?.in_progress ?? progress.filter(p => (p.status || '').toUpperCase() === 'IN_PROGRESS' || ((p.completed_pct || 0) > 0 && (p.completed_pct || 0) < 100)).length;

  return (
    <div className="space-y-3" data-testid="rcc-tab-training">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="rounded border border-amber-400/30 bg-amber-500/5 p-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">Assigned</p>
          <p className="text-xl font-bold text-white">{assignments.length}</p>
        </div>
        <div className="rounded border border-blue-400/30 bg-blue-500/5 p-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-blue-300">In Progress</p>
          <p className="text-xl font-bold text-white">{inProgress}</p>
        </div>
        <div className="rounded border border-emerald-400/30 bg-emerald-500/5 p-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Completed</p>
          <p className="text-xl font-bold text-white">{completed}</p>
        </div>
        <div className="rounded border border-purple-400/30 bg-purple-500/5 p-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-purple-300">Certificates</p>
          <p className="text-xl font-bold text-white">{certs.length}</p>
        </div>
      </div>

      {/* Assignments */}
      <Section title="Course Assignments">
        {assignments.length === 0 ? (
          <p className="text-slate-400 text-sm italic">No courses assigned.</p>
        ) : (
          <div className="space-y-2">
            {assignments.map(asg => {
              const p = progressByCourse[asg.course_id] || {};
              const pct = p.completed_pct || 0;
              const status = (p.status || asg.status || 'PENDING').toUpperCase();
              const tone = status === 'COMPLETED' ? 'lime' : status === 'IN_PROGRESS' ? 'amber' : 'blue';
              return (
                <div key={asg.id || asg.course_id} className="rounded border border-slate-700 bg-[#0E1626] p-2.5">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">{asg.course_title || asg.course_id}</p>
                      <p className="text-[11px] font-mono text-slate-500">{asg.course_id} {asg.assigned_at ? `· assigned ${new Date(asg.assigned_at).toLocaleDateString()}` : ''}</p>
                    </div>
                    <NxChip tone={tone}>{status.replace(/_/g, ' ')}</NxChip>
                  </div>
                  <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, Math.round(pct))}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-[11px] font-mono text-slate-400">{Math.round(pct)}% complete</p>
                    {p.best_exam_score != null && <p className="text-[11px] font-mono text-amber-300">Exam: {Math.round(p.best_exam_score)}%</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Certificates */}
      <Section title="Certificates Earned">
        {certs.length === 0 ? (
          <p className="text-slate-400 text-sm italic">No certificates yet.</p>
        ) : (
          <div className="space-y-2">
            {certs.map(c => (
              <div key={c.id || c.certificate_id} className="rounded border border-emerald-400/30 bg-emerald-500/5 p-2.5 flex items-start gap-2.5">
                <Award className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">{c.title || c.certificate_title || c.certification_code}</p>
                  <p className="text-[11px] font-mono text-slate-400">{c.certificate_id || c.id} · Issued {c.issued_at ? new Date(c.issued_at).toLocaleDateString() : '—'}</p>
                  {c.score != null && <p className="text-[11px] text-slate-300 mt-0.5">Final score: {Math.round(c.score)}%</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Permissions chips */}
      {data.user?.training_permissions && (
        <Section title="Training Permissions">
          <div className="flex flex-wrap gap-1.5">
            <NxChip tone={data.user.training_permissions.training_center_access ? 'lime' : 'red'}>
              {data.user.training_permissions.training_center_access ? 'TC Access' : 'No TC Access'}
            </NxChip>
            <NxChip tone="blue">Coach: {data.user.training_permissions.ai_coach_level || 'basic'}</NxChip>
            {Object.entries(data.user.training_permissions.modules || {}).filter(([_, v]) => v).map(([k]) => (
              <NxChip key={k} tone="amber">{k}</NxChip>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};


// -------------------------------------------------------------------
// IPS-OIE PSYCH EXAM — ADMIN REVIEW & GRADING
// -------------------------------------------------------------------
const FINAL_DECISIONS = [
  ['PASSED', 'Passed'],
  ['FAILED', 'Failed'],
  ['NEEDS_INTERVIEW_REVIEW', 'Needs Interview Review'],
  ['HOLD_FOR_SUPERVISOR_REVIEW', 'Hold for Supervisor Review'],
  ['PROCEED_TO_BACKGROUND', 'Proceed to Background Investigation'],
  ['PROCEED_CONDITIONAL_HIRE', 'Proceed with Conditional Hiring'],
  ['REJECT_APPLICATION', 'Reject Application'],
];

const PsychExamReview = ({ applicantId, onAfterChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState('');
  const [notes, setNotes] = useState('');
  const [interviewQs, setInterviewQs] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api(`/api/applicants/${applicantId}/psych-exam/review`);
      setData(r);
      setDecision(r?.result?.final_decision || '');
      setNotes(r?.result?.reviewer_notes || '');
      setInterviewQs(r?.result?.interview_questions || '');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [applicantId]);

  const unlock = async () => {
    setBusy(true);
    try {
      await api(`/api/applicants/${applicantId}/psych-exam/unlock`, {
        method: 'POST', body: JSON.stringify({ notify: true }) });
      toast.success('Exam unlocked · applicant notified');
      await load();
      onAfterChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const autoGrade = async () => {
    setBusy(true);
    try {
      await api(`/api/applicants/${applicantId}/psych-exam/auto-grade`, { method: 'POST' });
      toast.success('Auto-grade complete');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const saveManual = async (lock = false) => {
    if (!decision) { toast.error('Choose a final decision first'); return; }
    setBusy(true);
    try {
      await api(`/api/applicants/${applicantId}/psych-exam/manual-grade`, {
        method: 'PATCH',
        body: JSON.stringify({ final_decision: decision, reviewer_notes: notes,
                               interview_questions: interviewQs, lock }) });
      toast.success(lock ? 'Final grade locked' : 'Notes saved');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <p className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading exam review…</p>;

  const assignment = data?.assignment;
  const result = data?.result;
  const live = data?.live_grade;
  // If a graded result OR a submitted_at exists anywhere in the payload, treat
  // the exam as submitted even when no formal assignment record was minted
  // (applicants can self-submit via the portal without a prior admin assignment).
  const submittedAt = assignment?.submitted_at || result?.submitted_at || live?.submitted_at || data?.score?.submitted_at;
  const hasResult = !!(result || live);
  const status = submittedAt
    ? 'SUBMITTED'
    : (assignment?.status || (hasResult ? 'IN_REVIEW' : 'NOT_ASSIGNED'));
  const submitted = status === 'SUBMITTED';
  const responses = data?.responses || {};
  const answerKey = data?.answer_key || {};

  return (
    <div className="space-y-3" data-testid="rcc-tab-psych-admin">
      {/* Top Summary */}
      <Section title="IPS-OIE Exam — Status">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">Assignment</p>
            <p className="text-white font-mono">{status.replace(/_/g, ' ')}</p>
          </div>
          <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">Submitted</p>
            <p className="text-white">{submittedAt ? new Date(submittedAt).toLocaleString() : '—'}</p>
          </div>
          <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">Raw / Max</p>
            <p className="text-white font-bold">{result?.raw_score ?? live?.raw_score ?? '—'} / 520</p>
          </div>
          <div className="bg-slate-900/60 border border-slate-700 rounded p-2">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">Weighted / 100</p>
            <p className="text-white font-bold">{result?.weighted_score ?? live?.weighted_score ?? '—'}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {!assignment?.unlocked && !submitted && (
            <NxButton variant="primary" className="nx-btn--sm" onClick={unlock} disabled={busy} data-testid="psych-unlock-btn">
              <Unlock size={14} /> Approve for Testing (Unlock)
            </NxButton>
          )}
          {submitted && (
            <NxButton variant="secondary" className="nx-btn--sm" onClick={autoGrade} disabled={busy} data-testid="psych-auto-grade">
              <RefreshCw size={14} /> Auto Grade Exam
            </NxButton>
          )}
          {result?.locked && (
            <NxChip tone="red"><Lock size={11} /> Final grade locked</NxChip>
          )}
          {(result?.red_flag_review_required || live?.red_flag_review_required) && (
            <NxChip tone="amber"><AlertTriangle size={11} /> Red Flag Review Required</NxChip>
          )}
          {(result?.mandatory_board_review || live?.mandatory_board_review) && (
            <NxChip tone="red"><AlertTriangle size={11} /> Mandatory Board Review (8+ critical misses)</NxChip>
          )}
        </div>
      </Section>

      {/* Section scores */}
      {(result?.section_scores || live?.section_scores) && (
        <Section title="Section Scores">
          <div className="space-y-1.5">
            {Object.entries(result?.section_scores || live?.section_scores || {}).map(([s, r]) => (
              <div key={s} className="flex items-center gap-2 text-xs">
                <span className="text-slate-500 font-mono w-8">Sec{s}</span>
                <span className="text-white truncate w-44">{r.name}</span>
                <div className="flex-1 bg-slate-900 rounded h-2 overflow-hidden">
                  <div className={`h-full ${r.below_minimum ? 'bg-rose-500' : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(100, r.section_pct)}%` }} />
                </div>
                <span className="text-white font-mono w-16 text-right">{r.points}/{r.max}</span>
                <span className={`text-[10px] font-mono w-14 text-right ${r.below_minimum ? 'text-rose-300' : 'text-emerald-300'}`}>
                  {r.below_minimum ? 'BELOW MIN' : 'OK'}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Red flags */}
      {(result?.red_flags?.length > 0 || live?.red_flags?.length > 0) && (
        <Section title={`Red Flags (${(result?.red_flags || live?.red_flags || []).length})`}>
          {(result?.red_flags || live?.red_flags || []).map((f, i) => (
            <div key={i} className="text-xs text-rose-200 border-l-2 border-rose-400 pl-2 py-1">
              <span className="font-mono text-rose-400">[{f.code}]</span> {f.detail}
            </div>
          ))}
        </Section>
      )}

      {/* Question review */}
      {submitted && Object.keys(responses).length > 0 && (
        <Section title="Question Review (Admin Only)">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-300">
            <span className="font-mono">Total: {(live?.per_question || []).length} questions</span>
            <span className="font-mono text-emerald-300">· Correct: {(live?.per_question || []).filter(q => q.correct).length}</span>
            <span className="font-mono text-rose-300">· Wrong: {(live?.per_question || []).filter(q => !q.correct).length}</span>
            <span className="font-mono text-amber-300">· Critical misses: {(live?.per_question || []).filter(q => q.critical_miss).length}</span>
          </div>
          <div className="max-h-[65vh] overflow-y-auto space-y-1.5 text-[11px] border border-slate-700/60 rounded p-2 bg-[#0B1220]" data-testid="psych-question-review">
            {(live?.per_question || []).map((q) => {
              const correct = q.correct;
              return (
                <div key={q.n} className={`border-l-4 pl-2 py-1.5 ${q.critical_miss ? 'border-rose-500 bg-rose-500/10' : correct ? 'border-emerald-500/60 bg-emerald-500/5' : 'border-amber-500/50 bg-amber-500/5'}`}>
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="font-mono text-slate-500 w-9 flex-shrink-0">#{q.n}</span>
                    <span className="text-slate-400 text-[9px] uppercase font-mono">Sec{q.section}</span>
                    {q.critical && <span className="text-rose-300 text-[9px] font-mono bg-rose-500/20 px-1 rounded">CRIT</span>}
                    <span className="font-mono text-white ml-auto">{q.points_earned}/4</span>
                  </div>
                  <p className="text-white text-[12px] mt-1 break-words">{q.question}</p>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px]">
                    <div><span className="text-slate-500">Applicant:</span> <span className={correct ? 'text-emerald-300 font-semibold' : 'text-rose-300 font-semibold'}>{q.applicant_answer || '— no answer —'}</span></div>
                    <div><span className="text-slate-500">Preferred:</span> <span className="text-cyan-300 font-semibold">{q.preferred_answer}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-500 mt-2 font-mono">Answer key entries: {Object.keys(answerKey).length} · Critical Qs tracked: {data?.critical_questions?.length || 0}</p>
        </Section>
      )}

      {/* Manual grading */}
      {submitted && (
        <Section title="Manual Grading">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Final Decision</label>
          <select value={decision} onChange={(e) => setDecision(e.target.value)} disabled={result?.locked}
            className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 mt-1"
            data-testid="psych-final-decision">
            <option value="">— Select —</option>
            {FINAL_DECISIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mt-2 block">Reviewer Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={result?.locked}
            className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 min-h-[60px]"
            placeholder="Add review notes…" data-testid="psych-reviewer-notes" />
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mt-2 block">Board Interview Questions</label>
          <textarea value={interviewQs} onChange={(e) => setInterviewQs(e.target.value)} disabled={result?.locked}
            className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 min-h-[40px]"
            placeholder="Questions to ask in interview…" data-testid="psych-interview-qs" />
          <div className="mt-2 flex flex-wrap gap-2">
            <NxButton variant="secondary" className="nx-btn--sm" onClick={() => saveManual(false)} disabled={busy || result?.locked} data-testid="psych-save-notes">
              <Save size={14} /> Save Notes
            </NxButton>
            <NxButton variant="danger" className="nx-btn--sm" onClick={() => saveManual(true)} disabled={busy || result?.locked} data-testid="psych-lock-grade">
              <Lock size={14} /> Lock Final Grade
            </NxButton>
          </div>
        </Section>
      )}
    </div>
  );
};


// -------------------------------------------------------------------
// MESSAGE CENTER MODAL
// -------------------------------------------------------------------
const MSG_TEMPLATES = {
  login_credentials: { subject: 'NexusTrac Applicant Portal Login Credentials', body: 'Hello {{name}},\n\nYour NexusTrac Applicant Portal has been activated.\n\nUsername: {{username}}\nTemporary Password: {{temp_password}}\n\nPlease log in and complete any pending requirements.\n\nNexus Investigation Agency' },
  reset_password:    { subject: 'NexusTrac Password Reset', body: 'Hello {{name}},\n\nYour NexusTrac portal password has been reset.\n\nUsername: {{username}}\nTemporary Password: {{temp_password}}\n\nPlease log in and update your password.\n\nNexus Investigation Agency' },
  test_results:      { subject: 'NexusTrac Testing Status Update', body: 'Hello {{name}},\n\nYour testing status has been updated.\n\nPlease log into your NexusTrac portal for next steps.\n\nNexus Investigation Agency' },
  onboarding_docs:   { subject: 'NexusTrac Onboarding Documents Required', body: 'Hello {{name}},\n\nYou have onboarding documents pending in your NexusTrac portal.\n\nPlease log in and complete all required documents.\n\nNexus Investigation Agency' },
  interview_scheduled: { subject: 'Interview Scheduled', body: 'Hello {{name}},\n\nYour interview has been scheduled. Please check your portal for details.\n\nNexus Investigation Agency' },
  background_request:  { subject: 'Background Investigation — Additional Info Required', body: 'Hello {{name}},\n\nWe need additional information to proceed with your background investigation.\n\nPlease log into your portal and review pending requests.\n\nNexus Investigation Agency' },
  training_assigned:   { subject: 'Training Assignment Notice', body: 'Hello {{name}},\n\nNew training has been assigned to you in NexusTrac.\n\nPlease log in and review your training assignments.\n\nNexus Investigation Agency' },
  app_approved:        { subject: 'Application Approved', body: 'Hello {{name}},\n\nCongratulations — your application has been approved.\n\nNexus Investigation Agency' },
  app_rejected:        { subject: 'Application Update', body: 'Hello {{name}},\n\nThank you for your interest. After review, we are unable to proceed with your application at this time.\n\nNexus Investigation Agency' },
  conditional_offer:   { subject: 'Conditional Offer', body: 'Hello {{name}},\n\nWe are pleased to extend a conditional offer of employment, contingent on background and final approvals.\n\nNexus Investigation Agency' },
  general:             { subject: '', body: '' },
};

const MessageCenterModal = ({ open, onClose, applicantId, applicantName, applicantUsername }) => {
  const [method, setMethod] = useState('both');
  const [tplKey, setTplKey] = useState('general');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const tpl = MSG_TEMPLATES[tplKey] || { subject: '', body: '' };
    setSubject(tpl.subject.replace(/\{\{name\}\}/g, applicantName || 'Applicant'));
    setBody(tpl.body
      .replace(/\{\{name\}\}/g, applicantName || 'Applicant')
      .replace(/\{\{username\}\}/g, applicantUsername || ''));
  }, [tplKey, open, applicantName, applicantUsername]);

  const send = async () => {
    if (!subject || !body) { toast.error('Subject and body are required'); return; }
    setSending(true);
    try {
      await api(`/api/applicants/${applicantId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ delivery_method: method, message_type: tplKey,
                               subject, body, template_key: tplKey }),
      });
      toast.success('Message sent');
      onClose?.();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="msg-modal">
      <div className="bg-[#0F1B33] border border-amber-500/30 rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-700 px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300">Applicant Message Center</p>
            <h2 className="text-white text-base font-bold">Send to {applicantName}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="msg-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Delivery</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 mt-1" data-testid="msg-method">
                <option value="portal">Portal Notification Only</option>
                <option value="email">Email Only</option>
                <option value="both">Portal + Email</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Template</label>
              <select value={tplKey} onChange={(e) => setTplKey(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 mt-1" data-testid="msg-template">
                <option value="general">General Message</option>
                <option value="login_credentials">Login Credentials</option>
                <option value="reset_password">Reset Password</option>
                <option value="test_results">Test Results</option>
                <option value="onboarding_docs">Onboarding Documents</option>
                <option value="interview_scheduled">Interview Scheduled</option>
                <option value="background_request">Background Info Request</option>
                <option value="training_assigned">Training Assigned</option>
                <option value="app_approved">Application Approved</option>
                <option value="app_rejected">Application Rejected</option>
                <option value="conditional_offer">Conditional Offer</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 mt-1" data-testid="msg-subject" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Body</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded text-white text-xs p-2 mt-1 min-h-[160px]" data-testid="msg-body" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-700">
            <NxButton variant="ghost" onClick={onClose} className="nx-btn--sm" data-testid="msg-cancel">Cancel</NxButton>
            <NxButton variant="primary" onClick={send} disabled={sending} className="nx-btn--sm" data-testid="msg-send">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Message
            </NxButton>
          </div>
        </div>
      </div>
    </div>
  );
};


// -------------------------------------------------------------------
// PORTAL CONTROLS MENU — full action set, viewport-aware positioning
// -------------------------------------------------------------------
const PortalControlsMenu = ({ applicantId, full, onAfterChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });

  // Recompute menu position whenever it opens (viewport-aware)
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuH = 460; // approx max height
    const menuW = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuH && rect.top > menuH;
    const top = openUp ? Math.max(8, rect.top - menuH - 4) : rect.bottom + 4;
    let left = rect.left;
    if (left + menuW > window.innerWidth - 8) {
      left = window.innerWidth - menuW - 8;
    }
    left = Math.max(8, left);
    setPos({ top, left, openUp });
  }, [open]);

  const act = async (action, label) => {
    if (!window.confirm(`${label} for this applicant?`)) return;
    setBusy(true);
    try {
      await api(`/api/admin/applicants/${applicantId}/portal-control`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      toast.success(`${label} applied`);
      setOpen(false);
      onAfterChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const resetPw = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/admin/applicants/${applicantId}/reset-password`, { method: 'POST' });
      toast.success(`Password reset · temp: ${r.temp_password}`);
      setOpen(false);
      onAfterChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const repairLink = async () => {
    if (!window.confirm('Attempt to repair the user link by matching email/username?')) return;
    setBusy(true);
    try {
      const r = await api(`/api/admin/applicants/${applicantId}/repair-user-link`, { method: 'POST' });
      toast[r.success ? 'success' : 'error'](r.message || (r.success ? 'Link repaired' : 'Repair failed'));
      setOpen(false);
      onAfterChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const reprovision = async () => {
    const t = window.prompt('This will create a new user account. Type CONFIRM to proceed.');
    if ((t || '').trim().toUpperCase() !== 'CONFIRM') return;
    setBusy(true);
    try {
      const r = await api(`/api/admin/applicants/${applicantId}/reprovision-user`, { method: 'POST' });
      toast[r.success ? 'success' : 'error'](r.message);
      setOpen(false);
      onAfterChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // Each item in the dropdown
  const items = [
    { key: 'enable',   label: 'Activate Portal',         visible: full?.portal_active === false, onClick: () => act('enable',  'Activate portal'),  tone: 'emerald' },
    { key: 'disable',  label: 'Deactivate Portal',       visible: full?.portal_active !== false, onClick: () => act('disable', 'Deactivate portal'), tone: 'amber' },
    { key: 'lock',     label: 'Lock Account',            visible: !full?.portal_locked, onClick: () => act('lock',   'Lock account'),    tone: 'rose' },
    { key: 'unlock',   label: 'Unlock Account',          visible: !!full?.portal_locked, onClick: () => act('unlock', 'Unlock account'),  tone: 'emerald' },
    { key: 'reset-pw', label: 'Reset Password',          visible: true, onClick: resetPw, tone: 'slate' },
    { key: 'force',    label: 'Force Password Reset',    visible: !full?.require_password_change, onClick: () => act('force_password_change', 'Force password change'), tone: 'slate' },
    { key: 'clear',    label: 'Clear Force Reset',       visible: !!full?.require_password_change, onClick: () => act('clear_force', 'Clear force-password-change'), tone: 'slate' },
    { key: 'send-creds', label: 'Send Login Credentials', visible: true, onClick: () => { api(`/api/admin/applicants/${applicantId}/notice`, { method: 'POST', body: JSON.stringify({ template: 'temp_password' }) }).then(() => { toast.success('Credentials sent'); setOpen(false); }).catch(e => toast.error(e.message)); }, tone: 'slate' },
    { key: 'repair-link', label: 'Repair User Link',     visible: true, onClick: repairLink, tone: 'amber' },
    { key: 'reprovision', label: 'Re-Provision Portal User', visible: true, onClick: reprovision, tone: 'rose' },
  ];

  const toneClass = {
    emerald: 'text-emerald-300 hover:bg-emerald-500/10',
    rose:    'text-rose-300 hover:bg-rose-500/10',
    amber:   'text-amber-300 hover:bg-amber-500/10',
    slate:   'text-slate-200 hover:bg-slate-700/40',
  };

  return (
    <div className="relative" data-testid="rcc-portal-controls">
      <NxButton ref={btnRef} variant="secondary" className="nx-btn--sm" onClick={() => setOpen(o => !o)} disabled={busy} data-testid="rcc-portal-controls-btn">
        <ShieldCheck size={14} /> Portal Controls
      </NxButton>
      {open && (
        <>
          <div className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[81] w-60 rounded-lg border border-slate-700 bg-[#0E1626] shadow-2xl py-1 text-xs max-h-[80vh] overflow-y-auto"
            style={{ top: pos.top, left: pos.left }}
            data-testid="rcc-portal-controls-menu"
          >
            <div className="px-3 py-2 border-b border-slate-700/60">
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold">Portal Controls</p>
              <p className="text-[10px] text-slate-500 mt-0.5 font-mono">{full?.portal_username || 'no portal account'}</p>
            </div>
            {items.filter(i => i.visible).map(i => (
              <button
                key={i.key}
                onClick={i.onClick}
                disabled={busy}
                className={`block w-full text-left px-3 py-2 ${toneClass[i.tone]} disabled:opacity-50`}
                data-testid={`rcc-portal-${i.key}`}
              >
                {i.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
const APP_STATUSES = [
  'APPLIED', 'UNDER_REVIEW', 'APPROVED_FOR_TESTING', 'TESTING_PHASE',
  'TESTING_COMPLETED', 'INTERVIEW_SCHEDULED', 'INTERVIEW_COMPLETED',
  'BACKGROUND_INVESTIGATION', 'CONDITIONAL_OFFER', 'HIRED', 'REJECTED', 'ARCHIVED',
];

// -------------------------------------------------------------------
// DRAWER
// -------------------------------------------------------------------
const ApplicantDetailDrawer = ({ applicantId, onClose, onAfterChange }) => {
  const [tab, setTab] = useState('application');
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);

  const load = async () => {
    if (!applicantId) return;
    setLoading(true);
    try {
      const r = await api(`/api/applicants/${applicantId}`);
      setFull(r);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [applicantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStage = async (stage, label) => {
    setBusy(true);
    try {
      await api(`/api/applicants/${applicantId}/stage`, {
        method: 'PATCH', body: JSON.stringify({ stage }),
      });
      toast.success(`Stage → ${label}`);
      await load();
      onAfterChange?.();
    } catch (e) { toast.error(`Update failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  /**
   * The Hire action does TWO things atomically:
   *   1. Promotes the applicant into a full NexusTrac User account
   *      (role = field_investigator by default) — this is what gives them
   *      access to the field operations app, the Team page, and the
   *      promoted Training Center workflow.
   *   2. Sets the applicant's stage to HIRED (the promote-to-hired endpoint
   *      already does this on the server side, so we just reload after).
   * Idempotent: if the applicant already has a linked user, the backend
   * returns the existing user record instead of creating a duplicate.
   */
  const hire = async () => {
    if (full?.promoted_to_user_id) {
      // Defensive UI confirm — usually we won't get here because the Hire
      // button is conditionally hidden once promoted, but the data may have
      // gone stale.
      const ok = window.confirm(
        'This applicant is already linked to a NexusTrac user account. ' +
        'Re-running Hire will refresh the link but will NOT create a duplicate. Continue?'
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res = await api(`/api/admin/applicants/${applicantId}/promote-to-hired`, {
        method: 'POST',
        body: JSON.stringify({
          role: 'field_investigator',
          send_handbook_email: true,
          require_password_change: true,
          day_one_auto_assign: true,
        }),
      });
      if (res.already_promoted) {
        toast.success(`Already linked → user ${res.user?.username || res.promoted_to_user_id}`);
      } else {
        const cred = res.initial_password
          ? `username: ${res.user?.username} · temp pw: ${res.initial_password}`
          : `username: ${res.user?.username}`;
        toast.success(`Hired · NexusTrac account provisioned (${cred})`, { duration: 12000 });
      }
      await load();
      onAfterChange?.();
    } catch (e) {
      toast.error(`Hire failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/admin/applicants/${applicantId}/reset-password`, { method: 'POST' });
      toast.success(`Password reset · temp: ${r.temp_password || 'sent to applicant'}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // Iter121A — download a branded PDF export (application or psych exam).
  const downloadExport = async (kind) => {
    setBusy(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const path = kind === 'psych'
        ? `/api/applicants/${applicantId}/export-psych-pdf`
        : `/api/applicants/${applicantId}/export-application-pdf`;
      const res = await fetch(`${BACKEND_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = kind === 'psych'
        ? `nexus-psych-${applicantId}.pdf`
        : `nexus-application-${applicantId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${kind === 'psych' ? 'Psych exam' : 'Application'} PDF downloaded`);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Iter121A — permanent-delete an applicant (MASTER DIRECTIVE #29).
  // Department Admins may delete ANY applicant — pending, rejected or
  // even hired. If the applicant was promoted to a NexusTrac user, that
  // user account is preserved (deactivate from Team instead) so we don't
  // orphan case history.
  const deleteApplicant = async () => {
    const name = `${full?.first_name || ''} ${full?.last_name || ''}`.trim() || applicantId;
    const wasPromoted = !!full?.promoted_to_user_id;
    const ok = window.confirm(
      `Permanently delete applicant "${name}"?\n\n` +
      `This will remove the application, uploaded documents, and internal notes. ` +
      `It cannot be undone.\n\n` +
      (wasPromoted
        ? 'NOTE: The linked NexusTrac user account will NOT be deleted here. ' +
          'Deactivate it from the Team page if the person should also lose login access.'
        : '')
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/applicants/${applicantId}`, { method: 'DELETE' });
      toast.success('Applicant deleted');
      onAfterChange?.();
      onClose?.();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const stageLabel = STAGE_LABEL[full?.stage] || (full?.stage || '').replace(/_/g, ' ');
  const fullName = full ? `${full.first_name || ''} ${full.last_name || ''}`.trim() : 'Applicant';

  return (
    <NxPanel
      title={fullName || 'Applicant'}
      subtitle={`${full?.id || ''} · ${stageLabel}`}
      data-testid="rcc-detail-drawer"
      action={
        <button type="button" onClick={onClose} className="nx-btn nx-btn--icon" aria-label="Close" data-testid="rcc-close-detail">
          <X size={14} />
        </button>
      }
    >
      {loading ? (
        <p className="nx-body flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading applicant…</p>
      ) : !full ? (
        <p className="nx-body">Applicant not found.</p>
      ) : (
        <div className="space-y-3">
          {/* Quick chips */}
          <div className="flex flex-wrap items-center gap-2">
            <NxChip tone="blue">{stageLabel}</NxChip>
            {full.test_score?.weighted_pct != null && (
              <NxChip tone={full.test_score.weighted_pct >= 70 ? 'lime' : 'amber'}>
                IPS-OIE · {Math.round(full.test_score.weighted_pct)}%
              </NxChip>
            )}
            {full.risk_flag && <NxChip tone={full.risk_flag === 'HIGH' ? 'red' : full.risk_flag === 'LOW' ? 'lime' : 'amber'}>RISK · {full.risk_flag}</NxChip>}
            {full.portal_active && <NxChip tone="lime">Portal Active</NxChip>}
            <p className="text-[11px] font-mono text-slate-500 ml-auto">Username · {full.portal_username || 'n/a'}</p>
          </div>

          {/* Status dropdown */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="rcc-status-row">
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">Applicant Status</label>
            <select
              value={full.stage || ''}
              onChange={(e) => setStage(e.target.value, STAGE_LABEL[e.target.value] || e.target.value)}
              disabled={busy}
              className="bg-slate-900 border border-slate-700 rounded text-white text-xs px-2 py-1"
              data-testid="rcc-status-dropdown"
            >
              {Object.entries(STAGE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 flex-wrap border-b border-slate-700 pb-1 overflow-x-auto" data-testid="rcc-detail-tabs">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-t border-b-2 transition-colors ${tab === t.id ? 'border-amber-500 text-amber-300 bg-amber-500/5' : 'border-transparent text-slate-400 hover:text-white'}`}
                  data-testid={`rcc-tab-${t.id}`}
                >
                  <Icon className="w-3.5 h-3.5 inline mr-1" /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab body */}
          <div className="max-h-[calc(95vh-280px)] sm:max-h-[calc(95vh-260px)] overflow-y-auto pr-1">
            {tab === 'application' && <ApplicationTab a={full} applicantId={applicantId} onAfterChange={load} onOpenMessage={() => setShowMessageModal(true)} />}
            {tab === 'test' && (
              <div className="space-y-3">
                <TestTab a={full} applicantId={applicantId} onAfterChange={load} />
                <PsychExamReview applicantId={applicantId} onAfterChange={load} />
              </div>
            )}
            {tab === 'documents' && <DocumentsTab applicantId={applicantId} onAfterChange={load} />}
            {tab === 'training' && <TrainingTab a={full} applicantId={applicantId} />}
            {tab === 'interviews' && <InterviewsTab applicantId={applicantId} onAfterChange={load} />}
            {tab === 'background' && <BackgroundTab a={full} applicantId={applicantId} />}
            {tab === 'activity' && <ActivityTab a={full} />}
          </div>

          {/* Footer actions */}
          <div className="pt-2 border-t border-slate-700 flex flex-wrap items-center gap-2">
            <NxButton variant="primary" className="nx-btn--sm" onClick={() => setShowMessageModal(true)} data-testid="rcc-act-send-message">
              <MessagesSquare size={14} /> Send Message
            </NxButton>
            <NxButton variant="secondary" className="nx-btn--sm" onClick={resetPw} disabled={busy} data-testid="rcc-act-reset-pw">
              <KeyRound size={14} /> Reset PW
            </NxButton>
            <NxButton variant="secondary" className="nx-btn--sm" disabled={busy} onClick={() => api(`/api/admin/applicants/${applicantId}/notice`, { method: 'POST', body: JSON.stringify({ template: 'status_update' }) }).then(() => toast.success('Status update sent')).catch(e => toast.error(e.message))} data-testid="rcc-act-status-update">
              <Mail size={14} /> Send Status Update
            </NxButton>
            {/* Iter121A — Applicant PDF exports */}
            <NxButton variant="secondary" className="nx-btn--sm" disabled={busy} onClick={() => downloadExport('application')} data-testid="rcc-act-download-app-pdf" title="Download the full employment application as a branded PDF">
              <FileDown size={14} /> App PDF
            </NxButton>
            <NxButton
              variant="secondary" className="nx-btn--sm"
              disabled={busy || !(full?.test_score?.submitted_at)}
              onClick={() => downloadExport('psych')}
              data-testid="rcc-act-download-psych-pdf"
              title={full?.test_score?.submitted_at ? 'Download the IPS-OIE psych exam transcript' : 'Available after applicant submits the exam'}
            >
              <FileDown size={14} /> Psych PDF
            </NxButton>
            <PortalControlsMenu applicantId={applicantId} full={full} onAfterChange={load} />
            {/* Iter121A — Delete (any applicant — MASTER DIRECTIVE #29).
                Backend cascades applicant_documents + applicant_messages.
                Linked NexusTrac user (for hired applicants) is NOT deleted
                here — deactivate via the Team page to preserve case history. */}
            <NxButton
              variant="danger" className="nx-btn--sm" disabled={busy}
              onClick={deleteApplicant} data-testid="rcc-act-delete-applicant"
              title={full?.promoted_to_user_id
                ? 'Delete this applicant record. The linked user account stays — deactivate from Team if needed.'
                : 'Permanently delete this applicant record.'}
            >
              <Trash2 size={14} /> Delete
            </NxButton>
            <div className="ml-auto flex items-center gap-2">
              <NxButton variant="success" className="nx-btn--sm" disabled={busy} onClick={hire} data-testid="rcc-act-hire">
                <Award size={14} /> Hire
              </NxButton>
              <NxButton variant="danger" className="nx-btn--sm" disabled={busy} onClick={() => setStage('NOT_HIRED', 'Not Hired')} data-testid="rcc-act-not-hire">
                <AlertTriangle size={14} /> Not Hired
              </NxButton>
            </div>
          </div>
        </div>
      )}
      <MessageCenterModal
        open={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        applicantId={applicantId}
        applicantName={fullName}
        applicantUsername={full?.portal_username}
      />
    </NxPanel>
  );
};

export default ApplicantDetailDrawer;
